import { buildRelayablyInternalUrl } from '../api/relayablyInternalApi'
import {
  DashboardAgentDetailResponse,
  DashboardAgentUpdatePayload,
  DashboardAgentsResponse,
  DashboardBootstrapResponse,
  DashboardChatPayload,
  DashboardChatResponse,
  DashboardCallDetailResponse,
  DashboardCallsResponse,
  DashboardInstructionPayload,
  DashboardKnowledgeCaptureResponse,
  DashboardKnowledgeFilePayload,
  DashboardKnowledgeItemPayload,
  DashboardOverviewResponse,
} from './types'

const request = async <T>(path: string, sessionToken: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(buildRelayablyInternalUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-relayably-client': 'relayably-app',
      Authorization: `Bearer ${sessionToken}`,
      ...(options.headers ?? {}),
    },
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null
    throw new Error(body?.error?.message ?? 'Dashboard request failed.')
  }

  return (await response.json()) as T
}

export const relayablyDashboardApi = {
  async bootstrap(sessionToken: string): Promise<DashboardBootstrapResponse> {
    const [overview, calls, agents] = await Promise.all([
      request<DashboardOverviewResponse>('/internal/dashboard', sessionToken),
      request<DashboardCallsResponse>('/internal/calls', sessionToken),
      request<DashboardAgentsResponse>('/internal/agents', sessionToken),
    ])

    return {
      overview,
      calls: calls.calls,
      agents: agents.agents,
    }
  },

  async getCall(sessionToken: string, callId: string) {
    return request<DashboardCallDetailResponse>(`/internal/calls/${callId}`, sessionToken)
  },

  async getAgent(sessionToken: string, agentId: string) {
    return request<DashboardAgentDetailResponse>(`/internal/agents/${agentId}`, sessionToken)
  },

  async updateAgent(sessionToken: string, agentId: string, payload: DashboardAgentUpdatePayload) {
    return request<DashboardAgentDetailResponse>(`/internal/agents/${agentId}`, sessionToken, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  },

  async deployAgent(sessionToken: string, agentId: string, payload: DashboardAgentUpdatePayload) {
    return request<DashboardAgentDetailResponse>(`/internal/agents/${agentId}/deploy`, sessionToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async saveChat(sessionToken: string, payload: DashboardChatPayload) {
    return request<DashboardChatResponse>('/internal/chat', sessionToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async saveKnowledgeItem(sessionToken: string, payload: DashboardKnowledgeItemPayload) {
    return request<DashboardKnowledgeCaptureResponse>('/internal/knowledge-items', sessionToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async uploadKnowledgeFile(sessionToken: string, payload: DashboardKnowledgeFilePayload) {
    return request<DashboardKnowledgeCaptureResponse>('/internal/knowledge-items/file', sessionToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async deleteKnowledgeItem(sessionToken: string, knowledgeItemId: string) {
    return request<DashboardKnowledgeCaptureResponse>(`/internal/knowledge-items/${knowledgeItemId}`, sessionToken, {
      method: 'DELETE',
    })
  },

  async saveInstruction(sessionToken: string, payload: DashboardInstructionPayload) {
    return request<DashboardKnowledgeCaptureResponse>('/internal/instructions', sessionToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async deleteInstruction(sessionToken: string, instructionId: string) {
    return request<DashboardKnowledgeCaptureResponse>(`/internal/instructions/${instructionId}`, sessionToken, {
      method: 'DELETE',
    })
  },
}
